import { useMemo, useRef, useCallback, useEffect } from 'react'
import { LegalRepresentation } from '../../../models/classes/ApplicationStep3.class';
import { SolicitorTypeEnum } from '../../../models/enum/solicitor-type.enum';
import PrivateSolicitorForm from '../../../components/HOC/private-solicitor-form/private-solicitor-form';
import HdbSolicitorForm from '../../../components/HOC/hdb-solicitor-form/hdb-solicitor-form';
import { P2Accordion, P2TextLabel } from 'react-bootstrap-common-script';
import { LoanTypeEnum } from '../../../models/enum/loan-type.enum';
import Dropdown from '../../../components/dropdown/dropdown';
import { solicitorTypeItems } from '../../../utils/shared-dropdown';

interface LegalRepresentationProps {
  user_type: "current" | "proposed" | "outgoing";
  solicitor_type?: SolicitorTypeEnum | string;
  ConveyancingSetFormValue?: Function;
  legalRepresentation: LegalRepresentation;
  ConveyancingErrors?:any;
  loan_type?: LoanTypeEnum;
  showConveyancingDropdown?: boolean;
  handleConveyancingSelection?: Function;
  onConfirmCallback?: () => void;
  directly_conveyancing?: boolean,
  forceError?: boolean
}

function LegalRepresentationSegment({ ...props }: LegalRepresentationProps) {

  const showSecondConveyancing = useMemo(()=>{    
    if(props.solicitor_type === SolicitorTypeEnum.private_solicitor){
      return props.legalRepresentation.private_solicitor.is_confirm === true && props.legalRepresentation.private_solicitor.represented_same_solicitor === false;
    }

    if(props.solicitor_type === SolicitorTypeEnum.hdb_solicitor){
      return props.legalRepresentation.hdb_solicitor.is_confirm === true && props.legalRepresentation.hdb_solicitor.represented_same_solicitor === false;
    }
  },[props.legalRepresentation.hdb_solicitor.is_confirm, props.legalRepresentation.hdb_solicitor.represented_same_solicitor, props.legalRepresentation.private_solicitor.is_confirm, props.legalRepresentation.private_solicitor.represented_same_solicitor, props.solicitor_type])
  
  const dischargeLoanRef = useRef<any>(null);
  const conveyancingRef = useRef<any>(null);
  
  const title = useMemo(()=>{
    if(props.user_type === "current"){
      return "Lawyer for current owners' conveyancing";
    }
    else if(props.user_type === "proposed"){
      return "Lawyer for proposed owners' conveyancing";
    } 
    else if(props.user_type === "outgoing"){
      return "Lawyer for outgoing owners' conveyancing";
    }
    return "";
  },[props.user_type]);

  const handleToggle = useCallback(() => {
    dischargeLoanRef?.current?.toggle();
    if(props.onConfirmCallback){
      props.onConfirmCallback();
    }
  },[props])

  const handleToggleConveyancing = useCallback(() => {
    conveyancingRef?.current?.toggle();
    if(props.onConfirmCallback){
      props.onConfirmCallback();
    }
  },[props])

  const handleConveyancingSolicitorChange = useCallback((e: any) => {
    props?.handleConveyancingSelection?.(e);
  }, [props.handleConveyancingSelection])

  const selectedConveyancingSolicitor = useMemo(() => {
    if(props.legalRepresentation.is_conveyancing_hdb !== undefined) {
       return props.legalRepresentation.is_conveyancing_hdb ? SolicitorTypeEnum.hdb_solicitor : SolicitorTypeEnum.private_solicitor;
    }

    return undefined;
  }, [props.legalRepresentation])

  const secondConveyancingDropdownDisabled = useMemo(() => {
    if(props.showConveyancingDropdown) {
      if(props.legalRepresentation.is_conveyancing_hdb && props.legalRepresentation.hdb_solicitor.is_confirm) {
        return true;
      } else if(props.legalRepresentation.conveyancing_private_solicitor.is_confirm) {
        return true;
      }
    }
    return false;
  }, [props.legalRepresentation, props.showConveyancingDropdown])

  if(!props.solicitor_type && !props.directly_conveyancing){
    return <></>
  }
  return (
    <div className='polymer-legal-representation-segment'>
      {!props.directly_conveyancing && <>
        {props.solicitor_type !== "" && props.solicitor_type !== SolicitorTypeEnum.private_solicitor && <HdbSolicitorForm forceError={props.forceError} legal_representation={props.legalRepresentation} ConveyancingSetFormValue={props.ConveyancingSetFormValue} user_type={props.user_type} errors={props.ConveyancingErrors?.hdb_solicitor} loan_type={props.loan_type}/>}
        {props.solicitor_type !== "" && props.solicitor_type !== SolicitorTypeEnum.hdb_solicitor && <P2Accordion ref={dischargeLoanRef} defaultOpen={true} title={<P2TextLabel bold="700" label={"Private Lawyer"} />}>
          <PrivateSolicitorForm forceError={props.forceError} handleToggle={handleToggle} legal_representation={props.legalRepresentation} ConveyancingSetFormValue={props.ConveyancingSetFormValue} user_type={props.user_type} errors={props.ConveyancingErrors?.private_solicitor} loan_type={props.loan_type}/>
        </P2Accordion>}
      </>}


      {showSecondConveyancing && <div className='mt-3'>
        {!props.showConveyancingDropdown && <P2TextLabel bold="500" required color={"#333"} label={title}/>}
        <div className='mt-2'>
          {props.showConveyancingDropdown &&
            <div className='mb-3'>
              <Dropdown disabled={secondConveyancingDropdownDisabled} forceError={props.forceError} hasTooltip setSelectedDropdown={handleConveyancingSolicitorChange} selectedDropdown={selectedConveyancingSolicitor} label={`Lawyer for ${props.user_type} owners' conveyancing.`} placeholder='Please indicate lawyer type' required items={solicitorTypeItems} tooltipContent={<span>You would be required to engage a lawyer for the following:<div>1) Discharge of current mortgage loan</div><div>2) Obtaining fresh mortgage loan</div><div>3) Conveyancing for the ownership change (current owners)</div><div>4) Conveyancing for the ownership change (proposed owners)</div><br /><div>The change in flat ownership is a conveyancing process. You are recommended to have the same lawyer for process to expedite and reduce the need to liaise with multiple parties.</div></span>}/> 
            </div>
          }
          {props.legalRepresentation.is_conveyancing_hdb &&
           <div className='mb-3'>
            <HdbSolicitorForm forceError={props.forceError} legal_representation={props.legalRepresentation} ConveyancingSetFormValue={props.ConveyancingSetFormValue} user_type={props.user_type} errors={props.ConveyancingErrors?.hdb_solicitor} loan_type={props.loan_type} variant={"conveyancing"}/> 
           </div>
          }
          {
            (!props.showConveyancingDropdown || !props.legalRepresentation.is_conveyancing_hdb) &&            
              <P2Accordion
                defaultOpen={true}
                title={<P2TextLabel bold="700" label={"Private Lawyer"} />}
                ref={conveyancingRef}>
                <PrivateSolicitorForm forceError={props.forceError} handleToggle={handleToggleConveyancing} legal_representation={props.legalRepresentation} ConveyancingSetFormValue={props.ConveyancingSetFormValue} user_type={props.user_type} variant='conveyancing' errors={props.ConveyancingErrors?.conveyancing_private_solicitor} />
              </P2Accordion>
          }
        </div>
      </div>}
      
      {props.directly_conveyancing && <div className='mt-2'>
          {props.solicitor_type === SolicitorTypeEnum.conveyancing_hdb_solicitor && <HdbSolicitorForm forceError={props.forceError} legal_representation={props.legalRepresentation} ConveyancingSetFormValue={props.ConveyancingSetFormValue} user_type={props.user_type} errors={props.ConveyancingErrors?.hdb_solicitor} loan_type={props.loan_type} variant={"conveyancing"}/>}
          {props.solicitor_type === SolicitorTypeEnum.conveyancing_private_solicitor && <P2Accordion
            defaultOpen={true}
            title={<P2TextLabel bold="700" label={"Private Lawyer"} />}
            ref={conveyancingRef}
          >
            <PrivateSolicitorForm forceError={props.forceError} handleToggle={handleToggleConveyancing} legal_representation={props.legalRepresentation} ConveyancingSetFormValue={props.ConveyancingSetFormValue} user_type={props.user_type} variant='conveyancing' errors={props.ConveyancingErrors?.conveyancing_private_solicitor} />
          </P2Accordion>}
      </div>}
     
    </div>
  )
}

export default LegalRepresentationSegment
